import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface FleetProviderType {
  id: string;
  company_id: string;
  name: string;
  code: string;
  description?: string;
  icon?: string;
  color?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useFleetProviderTypes(companyId?: string) {
  return useQuery({
    queryKey: ["fleetProviderTypes", companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("fleet_provider_types")
        .select("*")
        .eq("company_id", companyId)
        .order("name");
      
      if (error) throw error;
      return data as FleetProviderType[];
    },
    enabled: !!companyId,
  });
}

export function useCreateFleetProviderType(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (typeData: Omit<FleetProviderType, 'id' | 'created_at' | 'updated_at' | 'company_id'>) => {
      const { data, error } = await supabase
        .from("fleet_provider_types")
        .insert([{ ...typeData, company_id: companyId }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetProviderTypes", companyId] });
      toast({
        title: "Sucesso",
        description: "Tipo de fornecedor criado com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao criar tipo de fornecedor.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateFleetProviderType(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...typeData }: Partial<FleetProviderType> & { id: string }) => {
      const { data, error } = await supabase
        .from("fleet_provider_types")
        .update(typeData)
        .eq("id", id)
        .eq("company_id", companyId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetProviderTypes", companyId] });
      toast({
        title: "Sucesso",
        description: "Tipo de fornecedor atualizado com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao atualizar tipo de fornecedor.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteFleetProviderType(companyId: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (typeId: string) => {
      // Verificar se há fornecedores usando este tipo
      const { data: providers } = await supabase
        .from("fleet_providers")
        .select("id")
        .eq("type", typeId)
        .eq("company_id", companyId);
      
      if (providers && providers.length > 0) {
        throw new Error(`Não é possível excluir. Existem ${providers.length} fornecedor(es) usando este tipo.`);
      }

      const { error } = await supabase
        .from("fleet_provider_types")
        .delete()
        .eq("id", typeId)
        .eq("company_id", companyId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetProviderTypes", companyId] });
      toast({
        title: "Sucesso",
        description: "Tipo de fornecedor excluído com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro",
        description: error.message || "Erro ao excluir tipo de fornecedor.",
        variant: "destructive",
      });
    },
  });
}
